import { candidateEnv } from './config/env';
import { genderKeyFromId } from '@/features/account/profile/personal-data/diversity/options';
import { getKeycloak } from './auth/keycloak-instance';

/**
 * EU-391 — Camada base do **dataLayer V2** (GTM & APIs de Conversão).
 *
 * Esta é a fundação técnica (helpers) da qual TODOS os eventos do épico
 * dependem (EU-392). Os EVENTOS em si vivem em `datalayer-events.ts`; o
 * `gtm.ts` só carrega o container. Aqui ficam:
 *  - helper central de `user_data` que envia os dados em texto limpo e converte
 *    qualquer dado não coletado para `null` (NUNCA `""`) — Advanced Matching /
 *    Enhanced Conversions;
 *  - gerador de `event_id` único por disparo (compartilhado Web/Server p/ dedup);
 *  - `detectSignupMethod` (method canônico GA4) — fonte única dos eventos de auth;
 *  - retenção do contexto da vaga (`opportunity_id/name`, `career_id/name`) ao
 *    longo do fluxo;
 *  - primitivas de push (`pushToDataLayer`/`isDataLayerEnabled`) e dedup.
 *
 * Convenção de nomes V2 (do spec EU-393): **`opportunity_*` = vaga (job)**;
 * **`career_*` = projeto/programa**. (É o INVERSO da convenção do Mixpanel, onde
 * `opportunity` = programa — não confundir.)
 *
 * Não passa por hashing client-side: o GTM/Server faz o hashing dos PII de
 * Advanced Matching. Aqui enviamos "texto limpo" conforme o spec.
 *
 * Mixpanel/RD Station são pipelines server-side independentes (BFF) — fora do
 * escopo destes eventos.
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/** `user_data` montado para Advanced Matching / Enhanced Conversions (DLR-002). */
export interface DataLayerUserData {
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  external_id: string | null;
  title: string | null;
  /** Data de nascimento no formato `YYYYMMDD`. */
  db: string | null;
  /** Gênero no formato Meta (`m`/`f`); `null` quando não-binário/não informado. */
  ge: 'm' | 'f' | null;
  street_address: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
}

/** Entrada bruta do `buildUserData` — todos os campos opcionais (parciais por fase). */
export interface DataLayerUserDataInput {
  email?: string | null;
  phone?: string | null;
  /** Nome completo — quebrado em `first_name` (1º token) + `last_name` (resto). */
  fullName?: string | null;
  /** `external_id` = `participant_id` = Keycloak `sub`. */
  participantId?: string | null;
  title?: string | null;
  /** Data de nascimento `YYYY-MM-DD` (SDK `birthDate`). */
  birthDate?: string | null;
  /** Id do gênero da reference table (SDK `genderId`). */
  genderId?: number | null;
  /** Logradouro (SDK `streetName`). */
  streetName?: string | null;
  /** Número (SDK `houseNumber`) — anexado ao `street_address`. */
  houseNumber?: string | null;
  /** Cidade (SDK `cityName`). */
  city?: string | null;
  /** UF / região (SDK `stateAcronym`). */
  region?: string | null;
  /** CEP (SDK `zipcode`). */
  postalCode?: string | null;
  /** Código do país ISO (SDK `countryCode`). */
  country?: string | null;
}

/** Contexto da vaga retido ao longo do fluxo (DLR-004). */
export interface DataLayerJobContext {
  opportunity_id: string | null;
  opportunity_name: string | null;
  career_id: string | null;
  career_name: string | null;
}

const JOB_CONTEXT_STORAGE_KEY = 'eureca_dl_job_context';

/** `''`/whitespace/`undefined` → `null`; senão a string trimada (texto limpo, sem `""`). */
export function emptyToNull(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = v.trim();
  return t === '' ? null : t;
}

/** `"YYYY-MM-DD"` → `"YYYYMMDD"`; `null` quando ausente/malformado. */
export function toYyyymmdd(isoDate: string | null | undefined): string | null {
  const v = emptyToNull(isoDate);
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (!m) return null;
  return `${m[1]}${m[2]}${m[3]}`;
}

/** Quebra o nome completo em `{ first, last }` (1º token vs. o resto); `null` quando vazio. */
export function splitFullName(fullName: string | null | undefined): {
  first: string | null;
  last: string | null;
} {
  const v = emptyToNull(fullName);
  if (!v) return { first: null, last: null };
  const parts = v.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: null, last: null };
  const first = parts[0] ?? null;
  const last = parts.length > 1 ? parts.slice(1).join(' ') : null;
  return { first, last };
}

/** `genderId` → `'m'`/`'f'` (Meta AM); `null` para não-binário/agênero/não informado. */
export function genderIdToMetaGe(genderId: number | null | undefined): 'm' | 'f' | null {
  if (genderId == null) return null;
  const key = genderKeyFromId(genderId);
  if (key === 'cis-male' || key === 'trans-male') return 'm';
  if (key === 'cis-female' || key === 'trans-female') return 'f';
  return null;
}

/** Junta logradouro + número num único `street_address`; `null` quando ambos vazios. */
function buildStreetAddress(
  streetName: string | null | undefined,
  houseNumber: string | null | undefined
): string | null {
  const street = emptyToNull(streetName);
  const number = emptyToNull(houseNumber);
  if (!street && !number) return null;
  if (street && number) return `${street}, ${number}`;
  return street ?? number;
}

/**
 * Monta o `user_data` (DLR-002) — texto limpo, qualquer campo não coletado vira
 * `null` (nunca `""`). Função PURA: os call-sites coletam os dados (token +
 * SDK) e passam aqui. Em fases iniciais (ex.: `sign_up`), os campos ainda não
 * coletados (phone/endereço/db/ge) saem como `null` legitimamente.
 */
export function buildUserData(input: DataLayerUserDataInput): DataLayerUserData {
  const { first, last } = splitFullName(input.fullName);
  return {
    email: emptyToNull(input.email),
    phone: emptyToNull(input.phone),
    first_name: first,
    last_name: last,
    external_id: emptyToNull(input.participantId),
    title: emptyToNull(input.title),
    db: toYyyymmdd(input.birthDate),
    ge: genderIdToMetaGe(input.genderId),
    street_address: buildStreetAddress(input.streetName, input.houseNumber),
    city: emptyToNull(input.city),
    region: emptyToNull(input.region),
    postal_code: emptyToNull(input.postalCode),
    country: emptyToNull(input.country),
  };
}

/**
 * `event_id` único por disparo (DLR-003). Compartilhado entre Web e Server para
 * o MESMO evento → o GTM/Server usa este id para desduplicar (ex.: o pixel
 * dispara Web + a Conversions API dispara Server com o mesmo `event_id`).
 */
export function generateEventId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Ambiente sem crypto.randomUUID — fallback suficientemente único.
    return `evt-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  }
}

/** O dataLayer V2 só empurra quando o container de EVENTOS está configurado (no-op caso contrário). */
export function isDataLayerEnabled(): boolean {
  return Boolean(candidateEnv.gtmContainerId?.trim());
}

/**
 * Push de baixo nível no dataLayer. No-op quando o GTM de eventos não está
 * configurado. Falha é apenas logada (não-fatal) — analytics nunca quebra o
 * fluxo do candidato. Garante `window.dataLayer = window.dataLayer ?? []` antes
 * de empurrar (o `initGtm` também inicializa, mas isto é defensivo).
 */
export function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  if (!isDataLayerEnabled()) return;
  try {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push(payload);
  } catch (err) {
    console.error('[eureca-datalayer] push failed (non-fatal)', err);
  }
}

/**
 * Resolve o `method` dos eventos de auth a partir do token Keycloak (claim
 * `identity_provider`/`idp`), em casing canônico GA4 (provider name humano):
 *  - `google` → `Google`; `linkedin-openid-connect`/`linkedin` → `LinkedIn`;
 *    default (email/senha) → `email`.
 * Fonte única consumida pelo `sign_up` e pelo `login`.
 */
export function detectSignupMethod(
  tokenParsed: Record<string, unknown>
): 'email' | 'Google' | 'LinkedIn' {
  const idpRaw = tokenParsed.identity_provider ?? tokenParsed.idp;
  const idp = typeof idpRaw === 'string' ? idpRaw.toLowerCase().trim() : '';
  if (idp === 'google') return 'Google';
  if (idp === 'linkedin-openid-connect' || idp === 'linkedin') return 'LinkedIn';
  return 'email';
}

/** Lê `{ participantId, email }` do token Keycloak; vazios quando deslogado/ausente. */
export function readAuthIdentity(): { participantId: string | null; email: string | null } {
  try {
    const kc = getKeycloak();
    const sub = kc.tokenParsed?.sub;
    const emailRaw = kc.tokenParsed?.email;
    if (kc.authenticated && typeof sub === 'string' && sub.length > 0) {
      return {
        participantId: sub,
        email: typeof emailRaw === 'string' && emailRaw.length > 0 ? emailRaw : null,
      };
    }
  } catch {
    /* antes do init do Keycloak — tratar como deslogado */
  }
  return { participantId: null, email: null };
}

// ───────────────────────────── Job context (DLR-004) ─────────────────────────

/** Retém o contexto da vaga em sessionStorage para os eventos downstream. */
export function setDataLayerJobContext(ctx: DataLayerJobContext): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(JOB_CONTEXT_STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    /* private mode / storage bloqueado */
  }
}

/** Lê o contexto da vaga retido; tudo `null` quando ausente. */
export function getDataLayerJobContext(): DataLayerJobContext {
  const empty: DataLayerJobContext = {
    opportunity_id: null,
    opportunity_name: null,
    career_id: null,
    career_name: null,
  };
  if (typeof window === 'undefined') return empty;
  try {
    const raw = sessionStorage.getItem(JOB_CONTEXT_STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<DataLayerJobContext>;
    return {
      opportunity_id: emptyToNull(parsed.opportunity_id),
      opportunity_name: emptyToNull(parsed.opportunity_name),
      career_id: emptyToNull(parsed.career_id),
      career_name: emptyToNull(parsed.career_name),
    };
  } catch {
    return empty;
  }
}

// ───────────────────────── Dedup helper (one-shot) ───────────────────────────

/**
 * Marca uma chave de dedup em sessionStorage e devolve `true` se foi a 1ª vez
 * (deve disparar) ou `false` se já marcada (re-render/reload → não redisparar).
 * Usado pelos eventos de disparo-único (view_job, generate_lead, qualified_lead,
 * sign_up, login). sessionStorage sobrevive a reload/re-render no mesmo tab
 * (DLR-012/034/044) — o backstop cross-sessão é o `event_id` (dedup Web/Server).
 */
export function markOnceInSession(dedupeKey: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (sessionStorage.getItem(dedupeKey) === '1') return false;
    sessionStorage.setItem(dedupeKey, '1');
    return true;
  } catch {
    // Sem storage não dá pra desduplicar de forma persistente; deixa disparar
    // (o `event_id` ainda desduplica no Server).
    return true;
  }
}
